import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, Observable, concat, fromEvent, interval } from 'rxjs';
import { filter, first, throttleTime } from 'rxjs/operators';

/** How often a running app looks for a new version. */
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * New versions of the installed shell — SPEC-2026-09-internal-chat-pwa (T-34 · RF-16 ·
 * RNF-08).
 *
 * A new build is announced, never applied behind the user's back: drafts live in memory,
 * so an automatic reload would throw away what they were typing. The user reloads from
 * the banner, on the same route. A worker that can no longer serve this version
 * (`unrecoverable`) gets a blocking banner, because nothing else will fix it.
 */
@Injectable({ providedIn: 'root' })
export class ChatAppUpdateService {

  private readonly updateReady = new BehaviorSubject<boolean>(false);
  private readonly unrecoverable = new BehaviorSubject<boolean>(false);

  readonly updateReady$: Observable<boolean> = this.updateReady.asObservable();
  readonly unrecoverable$: Observable<boolean> = this.unrecoverable.asObservable();

  private started = false;

  constructor(
    private readonly swUpdate: SwUpdate,
    private readonly appRef: ApplicationRef,
    private readonly zone: NgZone,
  ) { }

  start(): void {
    if (this.started || !this.swUpdate.isEnabled) return;
    this.started = true;

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => this.zone.run(() => this.updateReady.next(true)));

    this.swUpdate.unrecoverable
      .subscribe(() => this.zone.run(() => this.unrecoverable.next(true)));

    // Periodic checks only start once the app is stable, or it would never become so.
    const stable$ = this.appRef.isStable.pipe(first((isStable) => isStable));
    concat(stable$, interval(UPDATE_CHECK_INTERVAL_MS)).subscribe(() => this.check());

    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'), throttleTime(60_000))
      .subscribe(() => this.check());
  }

  /** Activates the downloaded version and reloads on the same address. */
  async reload(): Promise<void> {
    try {
      await this.swUpdate.activateUpdate();
    } finally {
      document.location.reload();
    }
  }

  private check(): void {
    this.swUpdate.checkForUpdate().catch(() => { /* offline — the next check retries */ });
  }
}
